import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail, API } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { HeartHandshake, Inbox, Check, X, Copy, Eye, Sparkles, Loader2, FileDown, Mail, FileText, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const ORIGIN = window.location.origin;
const TPL_AM = `${ORIGIN}/eurofind-am-template.pdf`;
const TPL_PM = `${ORIGIN}/eurofind-pm-template.pdf`;

function buildHelperEmail(name, code) {
  return `Subject: Welcome to EuroFind — your personal helper code

Dear ${name || "helper"},

Thank you for applying to help with EuroFind. Your application has been approved!

Your personal EuroFind helper code is: ${code}

Please keep this code safe — you will need it (together with your e-mail address) every time you upload a completed report sheet.

How it works:
1. Download the official report sheets:
   • Missing person (ante-mortem): ${TPL_AM}
   • Unidentified body (post-mortem): ${TPL_PM}
2. Fill in one sheet per case, as completely as possible.
3. Go to ${ORIGIN}/contribute, choose "I already have a code", verify with your e-mail and helper code, and upload the completed PDF(s). You can add up to 6 photos of the person per sheet — these will be shown with the case after approval.
4. Our team reviews every upload before it is placed on the platform.

Thank you for helping families find answers.

Warm regards,
The EuroFind team`;
}

function copyText(text, msg) {
  navigator.clipboard.writeText(text);
  toast.success(msg);
}

const KIND_META = {
  ante_mortem: { label: "Missing person", color: "#4A7C59" },
  post_mortem: { label: "Unidentified body", color: "#1F3A5F" },
};

const STATUS_COLOR = { pending: "#C99B4B", approved: "#4A7C59", rejected: "#9E4747", waiting: "#C99B4B" };

function StatusBadge({ s }) {
  const c = STATUS_COLOR[s] || "#6B7F82";
  return (
    <Badge variant="outline" className="rounded-full text-[11px] capitalize" style={{ borderColor: `${c}66`, color: c }}>
      {s}
    </Badge>
  );
}

export default function AdminHelpersPage() {
  const [helpers, setHelpers] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [codeDialog, setCodeDialog] = useState(null);
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [committing, setCommitting] = useState(false);

  const refresh = async () => {
    const [h, u] = await Promise.all([api.get("/admin/helpers"), api.get("/admin/helper-uploads")]);
    setHelpers(h.data);
    setUploads(u.data);
  };
  useEffect(() => { refresh(); }, []);

  const approve = async (h) => {
    setBusy(h.id);
    try {
      const { data } = await api.post(`/admin/helpers/${h.id}/approve`);
      setCodeDialog({ name: h.name, email: h.email, code: data.code });
      refresh();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Error"); }
    setBusy("");
  };

  const reject = async (h) => {
    if (!window.confirm(`Reject the application of ${h.name}?`)) return;
    await api.post(`/admin/helpers/${h.id}/reject`);
    refresh();
  };

  const extract = async (u) => {
    setBusy(u.id);
    try {
      const { data } = await api.post(`/admin/helper-uploads/${u.id}/extract`);
      setPreview({ ...data, upload: u });
      setSelected(new Set(data.records.map((_, i) => i)));
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "AI extraction failed"); }
    setBusy("");
  };

  const commit = async () => {
    const records = preview.records.filter((_, i) => selected.has(i));
    if (!records.length) return;
    const pp = preview.photo_paths || [];
    if (pp.length) records[0] = { ...records[0], photo_paths: pp };
    setCommitting(true);
    try {
      const { data } = await api.post("/admin/import/url/commit", {
        kind: preview.kind, records, label: preview.label, source_org: preview.source_org,
      });
      await api.post(`/admin/helper-uploads/${preview.upload.id}/resolve`, { status: "approved" });
      toast.success(`${data.created} created, ${(data.merged || []).length} merged into existing cases`);
      setPreview(null);
      refresh();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Import failed"); }
    setCommitting(false);
  };

  const rejectUpload = async (u) => {
    if (!window.confirm(`Reject "${u.filename}"?`)) return;
    await api.post(`/admin/helper-uploads/${u.id}/resolve`, { status: "rejected" });
    refresh();
  };

  const waiting = uploads.filter((u) => u.status === "waiting");
  const resolved = uploads.filter((u) => u.status !== "waiting");

  return (
    <div className="mx-auto max-w-5xl px-6 py-10" data-testid="admin-helpers-page">
      <div className="flex items-center gap-2">
        <HeartHandshake size={22} className="text-[#2E3A46]" />
        <h1 className="font-heading text-2xl font-semibold text-[#1A2024]">Helping hands</h1>
      </div>
      <p className="mt-1 font-body text-sm text-[#5C6873]">
        International contributors: approve applications, share their EuroFind code, and review uploaded report sheets.
      </p>

      <div className="mt-6 rounded-lg border border-[#E2E8F0] bg-white p-4" data-testid="report-sheets-box">
        <p className="font-body text-sm font-semibold text-[#1A2024]">Official report sheets</p>
        <p className="mt-0.5 font-body text-xs text-[#6B7F82]">Download to attach to your e-mail, or use the ready-made e-mail below which includes direct links.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <a href={TPL_AM} download data-testid="admin-tpl-am-download">
            <Button size="sm" variant="outline" className="border-[#4A7C59]/40 text-[#4A7C59] hover:bg-[#4A7C59]/5 hover:text-[#4A7C59]">
              <FileDown size={13} className="mr-1" /> Missing person (ante-mortem)
            </Button>
          </a>
          <a href={TPL_PM} download data-testid="admin-tpl-pm-download">
            <Button size="sm" variant="outline" className="border-[#1F3A5F]/40 text-[#1F3A5F] hover:bg-[#1F3A5F]/5 hover:text-[#1F3A5F]">
              <FileDown size={13} className="mr-1" /> Unidentified body (post-mortem)
            </Button>
          </a>
        </div>
      </div>

      <h2 className="mt-8 font-heading text-lg font-semibold text-[#1A2024]">Applications</h2>
      <div className="mt-3 space-y-2">
        {helpers.length === 0 && <p className="font-body text-sm text-[#8A96A0]" data-testid="no-helpers">No applications yet.</p>}
        {helpers.map((h) => (
          <div key={h.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-[#E2E8F0] bg-white p-4" data-testid={`helper-${h.id}`}>
            <div className="min-w-0 flex-1">
              <div className="font-body text-sm font-semibold text-[#1A2024]">{h.name}
                {h.code && (
                  <button type="button" title="Click to copy code" data-testid={`copy-inline-code-${h.id}`}
                    onClick={() => copyText(h.code, "Code copied")}
                    className="ml-2 inline-flex items-center gap-1 rounded bg-[#2E3A46]/8 px-1.5 py-0.5 font-mono text-xs text-[#2E3A46] hover:bg-[#2E3A46]/15">
                    {h.code} <Copy size={10} />
                  </button>
                )}
              </div>
              <div className="font-body text-xs text-[#6B7F82]">{h.email}{h.organization ? ` · ${h.organization}` : ""}{h.scope ? ` · ${h.scope}` : ""}</div>
              {h.status === "approved" && h.upload_stats && (
                <div className="mt-1 flex flex-wrap items-center gap-1.5 font-body text-[11px]" data-testid={`helper-stats-${h.id}`}>
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#2E3A46]/8 px-2 py-0.5 text-[#2E3A46]">
                    <FileText size={10} /> {h.upload_stats.total} sheet{h.upload_stats.total === 1 ? "" : "s"}
                  </span>
                  {h.upload_stats.approved > 0 && <span className="rounded-full bg-[#4A7C59]/10 px-2 py-0.5 text-[#4A7C59]">{h.upload_stats.approved} approved</span>}
                  {h.upload_stats.waiting > 0 && <span className="rounded-full bg-[#C99B4B]/15 px-2 py-0.5 text-[#9a7436]">{h.upload_stats.waiting} waiting</span>}
                  {h.upload_stats.rejected > 0 && <span className="rounded-full bg-[#9E4747]/10 px-2 py-0.5 text-[#9E4747]">{h.upload_stats.rejected} rejected</span>}
                </div>
              )}
            </div>
            <StatusBadge s={h.status} />
            {h.status === "pending" && (
              <>
                <Button size="sm" disabled={busy === h.id} onClick={() => approve(h)} className="bg-[#4A7C59] text-white hover:bg-[#3d6749]" data-testid={`approve-helper-${h.id}`}>
                  <Check size={13} className="mr-1" /> Approve
                </Button>
                <Button size="sm" variant="outline" onClick={() => reject(h)} className="border-[#9E4747]/40 text-[#9E4747] hover:bg-[#9E4747]/5 hover:text-[#9E4747]" data-testid={`reject-helper-${h.id}`}>
                  <X size={13} className="mr-1" /> Reject
                </Button>
              </>
            )}
            {h.status === "approved" && h.code && (
              <Button size="sm" variant="outline" className="border-[#E2E8F0]" onClick={() => setCodeDialog({ name: h.name, email: h.email, code: h.code })} data-testid={`show-code-${h.id}`}>
                <Mail size={13} className="mr-1" /> E-mail & code
              </Button>
            )}
          </div>
        ))}
      </div>

      <h2 className="mt-10 flex items-center gap-2 font-heading text-lg font-semibold text-[#1A2024]">
        <Inbox size={18} /> Waiting box
        {waiting.length > 0 && <Badge className="rounded-full bg-[#C99B4B] text-white">{waiting.length}</Badge>}
      </h2>
      <div className="mt-3 space-y-2">
        {waiting.length === 0 && <p className="font-body text-sm text-[#8A96A0]" data-testid="no-waiting">No sheets waiting for review.</p>}
        {waiting.map((u) => {
          const km = KIND_META[u.kind];
          return (
            <div key={u.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-[#E2E8F0] bg-white p-4" data-testid={`upload-${u.id}`}>
              <div className="min-w-0 flex-1">
                <div className="truncate font-body text-sm font-semibold text-[#1A2024]">{u.filename}</div>
                <div className="font-body text-xs text-[#6B7F82]">
                  {u.helper_name} ({u.helper_code}) · {(u.uploaded_at || "").slice(0, 10)}
                  {(u.photo_paths || []).length > 0 && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#2E3A46]/8 px-1.5 py-0.5 text-[#2E3A46]" data-testid={`upload-photos-count-${u.id}`}><ImageIcon size={10} /> {u.photo_paths.length} photo{u.photo_paths.length === 1 ? "" : "s"}</span>}
                </div>
              </div>
              <Badge variant="outline" className="rounded-full text-[11px]" style={{ borderColor: `${km.color}66`, color: km.color }}>{km.label}</Badge>
              {u.kind_autocorrected && (
                <Badge variant="outline" className="rounded-full border-[#C99B4B]/60 text-[11px] text-[#9a7436]" title="The sheet type in the PDF did not match the category the helper selected — corrected automatically." data-testid={`kind-corrected-${u.id}`}>
                  auto-corrected
                </Badge>
              )}
              <a href={`${API}/admin/helper-uploads/${u.id}/file`} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline" className="border-[#E2E8F0]" data-testid={`view-upload-${u.id}`}><Eye size={13} className="mr-1" /> View PDF</Button>
              </a>
              <Button size="sm" disabled={busy === u.id} onClick={() => extract(u)} className="bg-[#2E3A46] text-white hover:bg-[#1A2024]" data-testid={`process-upload-${u.id}`}>
                {busy === u.id ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Sparkles size={13} className="mr-1" />} Process (AI preview)
              </Button>
              <Button size="sm" variant="outline" onClick={() => rejectUpload(u)} className="border-[#9E4747]/40 text-[#9E4747] hover:bg-[#9E4747]/5 hover:text-[#9E4747]" data-testid={`reject-upload-${u.id}`}>
                <X size={13} />
              </Button>
            </div>
          );
        })}
      </div>

      {resolved.length > 0 && (
        <>
          <h2 className="mt-10 font-heading text-lg font-semibold text-[#1A2024]">Handled</h2>
          <div className="mt-3 space-y-2">
            {resolved.slice(0, 20).map((u) => (
              <div key={u.id} className="flex items-center gap-3 rounded-lg border border-[#E2E8F0] bg-[#F8F9FA] p-3">
                <div className="min-w-0 flex-1 truncate font-body text-sm text-[#5C6873]">{u.filename} · {u.helper_name}</div>
                <StatusBadge s={u.status} />
              </div>
            ))}
          </div>
        </>
      )}

      <Dialog open={!!codeDialog} onOpenChange={(o) => !o && setCodeDialog(null)}>
        <DialogContent className="max-h-[85vh] max-w-xl overflow-y-auto" data-testid="helper-code-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">EuroFind code for {codeDialog?.name}</DialogTitle>
            <DialogDescription>
              Send this to {codeDialog?.email}. The ready-made e-mail below contains the code, links to both report sheets and the upload instructions — copy it in one click.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-between rounded-lg border border-[#E2E8F0] bg-[#F8F9FA] p-4">
            <span className="font-mono text-2xl font-semibold text-[#1A2024]" data-testid="helper-code-value">{codeDialog?.code}</span>
            <Button size="sm" variant="outline" className="border-[#E2E8F0]" data-testid="copy-code-btn"
              onClick={() => copyText(codeDialog?.code || "", "Code copied")}>
              <Copy size={13} className="mr-1" /> Copy code
            </Button>
          </div>
          <div className="rounded-lg border border-[#E2E8F0] bg-white">
            <div className="flex items-center justify-between border-b border-[#E2E8F0] px-3 py-2">
              <span className="font-body text-xs font-semibold text-[#5C6873]">Ready-made e-mail (English)</span>
              <Button size="sm" className="bg-[#2E3A46] text-white hover:bg-[#1A2024]" data-testid="copy-email-btn"
                onClick={() => copyText(buildHelperEmail(codeDialog?.name, codeDialog?.code), "Full e-mail copied — paste it into your mail client")}>
                <Copy size={13} className="mr-1" /> Copy full e-mail
              </Button>
            </div>
            <pre className="max-h-64 overflow-y-auto whitespace-pre-wrap px-3 py-2 font-body text-xs leading-relaxed text-[#1A2024]" data-testid="helper-email-preview">
              {buildHelperEmail(codeDialog?.name, codeDialog?.code)}
            </pre>
          </div>
          <div className="flex flex-wrap gap-2">
            <a href={TPL_AM} download>
              <Button size="sm" variant="outline" className="border-[#4A7C59]/40 text-[#4A7C59] hover:bg-[#4A7C59]/5 hover:text-[#4A7C59]" data-testid="dialog-tpl-am-download">
                <FileDown size={13} className="mr-1" /> Ante-mortem PDF
              </Button>
            </a>
            <a href={TPL_PM} download>
              <Button size="sm" variant="outline" className="border-[#1F3A5F]/40 text-[#1F3A5F] hover:bg-[#1F3A5F]/5 hover:text-[#1F3A5F]" data-testid="dialog-tpl-pm-download">
                <FileDown size={13} className="mr-1" /> Post-mortem PDF
              </Button>
            </a>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="upload-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">AI preview — {preview?.upload?.filename}</DialogTitle>
            <DialogDescription>Review the extracted data, then confirm to place it on the platform (duplicates are merged automatically).</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {(preview?.photo_paths || []).length > 0 && (
              <div data-testid="preview-photos">
                <p className="font-body text-xs font-semibold text-[#5C6873]">Photos supplied by the helper (attached to the first selected record on import):</p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {preview.photo_paths.map((p) => (
                    <a key={p} href={`${API}/files/${p}`} target="_blank" rel="noreferrer">
                      <img src={`${API}/files/${p}?thumb=1`} alt="helper upload" className="h-16 w-16 rounded-md border border-[#E2E8F0] object-cover" />
                    </a>
                  ))}
                </div>
              </div>
            )}
            {(preview?.records || []).length === 0 && <p className="font-body text-sm text-[#9E4747]">No records could be extracted from this sheet.</p>}
            {(preview?.records || []).map((r, i) => (
              <label key={i} className="flex items-start gap-3 rounded-lg border border-[#E2E8F0] p-3" data-testid={`preview-record-${i}`}>
                <Checkbox checked={selected.has(i)} onCheckedChange={(v) => setSelected((prev) => { const n = new Set(prev); v ? n.add(i) : n.delete(i); return n; })} className="mt-0.5" />
                <div className="min-w-0 flex-1">
                  <div className="font-body text-sm font-semibold text-[#1A2024]">{r.full_name || r.case_name || r.reference_code || `Record ${i + 1}`}</div>
                  <div className="mt-1 font-body text-xs text-[#5C6873]">
                    {[r.gender, r.age || (r.age_min != null ? `${r.age_min}-${r.age_max ?? r.age_min} yrs` : null),
                      r.height_cm ? `${r.height_cm} cm` : null, r.country, r.location, r.event_date]
                      .filter(Boolean).join(" · ") || "—"}
                  </div>
                  {(r.distinguishing_marks || r.circumstances || r.description) && (
                    <div className="mt-1 line-clamp-2 font-body text-xs text-[#8A96A0]">{r.distinguishing_marks || r.circumstances || r.description}</div>
                  )}
                </div>
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" className="border-[#E2E8F0]" onClick={() => setPreview(null)}>Cancel</Button>
            <Button disabled={committing || selected.size === 0} onClick={commit} className="bg-[#4A7C59] text-white hover:bg-[#3d6749]" data-testid="confirm-import-btn">
              {committing ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Check size={14} className="mr-1" />}
              Confirm import ({selected.size})
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
